import React, { useEffect } from 'react';

import { useNavigate } from 'react-router-dom';
import { useTypedSearchParams } from 'react-router-typesafe-routes';
import { Notification } from '@ergeon/core-components';

import Layout from '../../components/Layout';
import Header from '../../components/Header';
import Navigation from '../../components/Navigation';
import { CallInfo, CallsLister } from '../../components/Calls';
import CallInfoSkeleton from '../../components/Calls/CallInfoSkeleton';
import { CallNotFoundEmptyState } from '../../components/EmptyStates';
import { ROUTES } from '../../routes';
import useCallInfoBuilder from '../../hooks/useCallInfoBuilder';

export const CallNewPageContent = () => {
  const navigate = useNavigate();
  const [{ sid }] = useTypedSearchParams(ROUTES.callNew);

  const { callInfo, isLoading, error, foundCallId } = useCallInfoBuilder({
    sid,
    stopOnCallId: true,
  });

  // Redirect when callId is found
  useEffect(() => {
    if (foundCallId) {
      navigate(ROUTES.call.$buildPath({ params: { callId: foundCallId } }), { replace: true });
    }
  }, [foundCallId, navigate]);

  // Handle missing SID parameter
  if (!sid) {
    return (
      <Layout>
        <Layout.TopBar>
          <Header />
        </Layout.TopBar>
        <Layout.Body>
          <Layout.SideBar>
            <Navigation />
          </Layout.SideBar>
          <Layout.List>
            <CallsLister selectedCallId={foundCallId ?? undefined} />
          </Layout.List>
          <Layout.Content>
            <CallNotFoundEmptyState />
          </Layout.Content>
        </Layout.Body>
      </Layout>
    );
  }

  const renderContent = () => {
    // Show error if data fetch failed
    if (error && !callInfo.hasData()) {
      return (
        <>
          <Notification mode="embed" type="Error">
            {error}
          </Notification>
          <CallNotFoundEmptyState />
        </>
      );
    }

    // Show call info if we have data
    if (callInfo.hasData()) {
      return (
        <>
          <CallInfo data={callInfo} />
          {isLoading && (
            <Notification mode="embed" type="Information">
              Waiting for call record to be created...
            </Notification>
          )}
        </>
      );
    }

    // Show skeleton while loading
    if (isLoading) {
      return (
        <>
          <CallInfoSkeleton />
          <Notification mode="embed" type="Information">
            Waiting for call record to be created...
          </Notification>
        </>
      );
    }

    // Fallback to not found
    return <CallNotFoundEmptyState />;
  };

  return (
    <Layout>
      <Layout.TopBar>
        <Header />
      </Layout.TopBar>
      <Layout.Body>
        <Layout.SideBar>
          <Navigation />
        </Layout.SideBar>
        <Layout.List>
          <CallsLister />
        </Layout.List>
        <Layout.Content>
          {renderContent()}
        </Layout.Content>
      </Layout.Body>
    </Layout>
  );
};
